/**
 * IOSInstallPrompt Component
 *
 * A wrapper around react-ios-pwa-prompt that handles browser compatibility.
 * On iOS < 16.4, PWAs can only be installed from Safari.
 * On iOS >= 16.4, PWAs can be installed from any browser.
 *
 * This component detects the browser and iOS version to show appropriate
 * instructions, avoiding misleading users on unsupported browsers.
 */

import { useState, useEffect, useCallback, type CSSProperties } from 'react';
import PWAPrompt from 'react-ios-pwa-prompt';

/**
 * Detects if the device is running iOS
 */
function isIOS(): boolean {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return false;
  }

  const userAgent = navigator.userAgent;

  // Check for iOS devices
  // iPad on iOS 13+ reports as Mac, so we also check for touch support
  const isIOSDevice = /iPad|iPhone|iPod/.test(userAgent);
  const isIPadOS =
    navigator.platform === 'MacIntel' &&
    typeof navigator.maxTouchPoints === 'number' &&
    navigator.maxTouchPoints > 1;

  return isIOSDevice || isIPadOS;
}

/**
 * Detects if the browser is Safari on iOS
 * All iOS browsers use WebKit, but only Safari is the "real" Safari
 */
function isIOSSafari(): boolean {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return false;
  }

  const userAgent = navigator.userAgent;

  // All iOS browsers have Safari in UA, but non-Safari browsers also have
  // identifiers like CriOS (Chrome), FxiOS (Firefox), EdgiOS (Edge), etc.
  const isSafariUA = /Safari/.test(userAgent);
  const isChrome = /CriOS/.test(userAgent);
  const isFirefox = /FxiOS/.test(userAgent);
  const isEdge = /EdgiOS/.test(userAgent);
  const isOpera = /OPiOS/.test(userAgent);
  const isBrave = /Brave/.test(userAgent);

  return isSafariUA && !isChrome && !isFirefox && !isEdge && !isOpera && !isBrave;
}

/**
 * Gets the iOS version number
 * Returns null if not iOS or version cannot be determined
 */
function getIOSVersion(): { major: number; minor: number } | null {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return null;
  }

  const userAgent = navigator.userAgent;

  // Match iOS version from user agent
  // Format: "OS X_Y" or "OS X_Y_Z"
  const match = userAgent.match(/OS (\d+)_(\d+)/);

  if (match && match[1] && match[2]) {
    return {
      major: parseInt(match[1], 10),
      minor: parseInt(match[2], 10),
    };
  }

  return null;
}

/**
 * Checks if the iOS version supports PWA installation from any browser
 * iOS 16.4+ allows PWA installation from Chrome, Firefox, Edge, etc.
 */
function supportsNonSafariPWAInstall(): boolean {
  const version = getIOSVersion();

  if (!version) {
    // If we can't determine version, assume older iOS (safer)
    return false;
  }

  // iOS 16.4 and later support PWA install from any browser
  return version.major > 16 || (version.major === 16 && version.minor >= 4);
}

/**
 * Gets the name of the current browser for display
 */
function getBrowserName(): string {
  if (typeof navigator === 'undefined') return 'this browser';

  const userAgent = navigator.userAgent;

  if (/CriOS/.test(userAgent)) return 'Chrome';
  if (/FxiOS/.test(userAgent)) return 'Firefox';
  if (/EdgiOS/.test(userAgent)) return 'Edge';
  if (/OPiOS/.test(userAgent)) return 'Opera';
  if (/Brave/.test(userAgent)) return 'Brave';

  return 'this browser';
}

/**
 * Check if app is already installed as PWA
 */
function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;

  // Check standard standalone mode
  if (window.matchMedia('(display-mode: standalone)').matches) {
    return true;
  }

  // Check iOS standalone mode
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  if ((navigator as any).standalone === true) {
    return true;
  }

  return false;
}

/**
 * Check if prompt was dismissed within the cooldown period
 */
function wasDismissedRecently(): boolean {
  try {
    const dismissedAt = localStorage.getItem('ios-pwa-safari-prompt-dismissed');
    if (!dismissedAt) return false;

    // 7-day cooldown
    const dismissedDate = new Date(dismissedAt);
    const daysSinceDismissal = (Date.now() - dismissedDate.getTime()) / (1000 * 60 * 60 * 24);
    return daysSinceDismissal < 7;
  } catch {
    return false;
  }
}

/**
 * IOSInstallPrompt Component Props
 */
export interface IOSInstallPromptProps {
  /** Delay before showing the prompt in ms */
  delay?: number;
  /** Called when the prompt is closed */
  onClose?: () => void;
}

/**
 * IOSInstallPrompt shows the appropriate PWA installation prompt based on
 * the user's browser and iOS version.
 *
 * - Safari: Shows the standard react-ios-pwa-prompt
 * - Non-Safari on iOS 16.4+: Shows the standard react-ios-pwa-prompt
 * - Non-Safari on iOS < 16.4: Shows a message directing users to Safari
 */
export const IOSInstallPrompt = ({ delay = 1000, onClose }: IOSInstallPromptProps) => {
  const [showSafariPrompt, setShowSafariPrompt] = useState(false);

  // Determine if we should show the "open in Safari" prompt
  const shouldShowSafariPrompt = useCallback((): boolean => {
    // Not iOS - don't show anything (let InstallPrompt handle it)
    if (!isIOS()) return false;

    // Already installed - don't show
    if (isStandalone()) return false;

    // Safari - use the standard PWAPrompt component instead
    if (isIOSSafari()) return false;

    // Non-Safari but iOS 16.4+ supports install - use standard PWAPrompt
    if (supportsNonSafariPWAInstall()) return false;

    // Recently dismissed
    if (wasDismissedRecently()) return false;

    // Non-Safari on older iOS - need to show Safari redirect prompt
    return true;
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (shouldShowSafariPrompt()) {
        setShowSafariPrompt(true);
      }
    }, delay);

    return () => clearTimeout(timer);
  }, [delay, shouldShowSafariPrompt]);

  const handleDismiss = () => {
    try {
      localStorage.setItem('ios-pwa-safari-prompt-dismissed', new Date().toISOString());
    } catch {
      // Ignore localStorage errors
    }
    setShowSafariPrompt(false);
    onClose?.();
  };

  // Show custom "open in Safari" prompt for non-Safari on older iOS
  if (showSafariPrompt) {
    const browserName = getBrowserName();

    return (
      <div style={overlayStyle}>
        <div style={modalStyle}>
          <div style={headerStyle}>
            <h2 style={titleStyle}>Install Flash</h2>
            <button onClick={handleDismiss} style={closeButtonStyle} aria-label="Close">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path
                  d="M18 6L6 18M6 6l12 12"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          </div>

          <div style={bodyStyle}>
            <div style={iconContainerStyle}>
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="10" stroke="#f59e0b" strokeWidth="2" />
                <path
                  d="M12 8v4M12 16h.01"
                  stroke="#f59e0b"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </div>

            <p style={messageStyle}>
              <strong>{browserName}</strong> doesn&apos;t support installing apps on this version of
              iOS.
            </p>

            <p style={instructionStyle}>
              To install Flash on your home screen, please open this page in <strong>Safari</strong>
              .
            </p>

            <div style={stepsContainerStyle}>
              <p style={stepTitleStyle}>How to open in Safari:</p>
              <ol style={stepsListStyle}>
                <li style={stepStyle}>Copy this page&apos;s URL from the address bar</li>
                <li style={stepStyle}>Open the Safari app</li>
                <li style={stepStyle}>Paste the URL and visit the page</li>
                <li style={stepStyle}>
                  Tap the Share button{' '}
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    style={{ verticalAlign: 'middle' }}
                  >
                    <path
                      d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z"
                      fill="#2563eb"
                    />
                    <path
                      d="M12 4v12M12 4l-4 4M12 4l4 4M4 20h16"
                      stroke="#2563eb"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>{' '}
                  then &quot;Add to Home Screen&quot;
                </li>
              </ol>
            </div>
          </div>

          <div style={footerStyle}>
            <button onClick={handleDismiss} style={dismissButtonStyle}>
              Maybe later
            </button>
          </div>
        </div>
      </div>
    );
  }

  // For Safari or iOS 16.4+ non-Safari browsers, show the standard PWAPrompt
  // The PWAPrompt library handles its own iOS/standalone detection
  return <PWAPrompt />;
};

// Styles
const overlayStyle: CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  display: 'flex',
  alignItems: 'flex-end',
  justifyContent: 'center',
  zIndex: 10000,
  padding: '16px',
};

const modalStyle: CSSProperties = {
  backgroundColor: 'var(--color-bg-primary)',
  borderRadius: '16px 16px 0 0',
  width: '100%',
  maxWidth: '400px',
  maxHeight: '90vh',
  overflow: 'auto',
  boxShadow: 'var(--shadow-lg)',
};

const headerStyle: CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '16px 16px 0 16px',
};

const titleStyle: CSSProperties = {
  margin: 0,
  fontSize: '1.25rem',
  fontWeight: 600,
  color: 'var(--color-text-primary)',
};

const closeButtonStyle: CSSProperties = {
  background: 'none',
  border: 'none',
  padding: '8px',
  cursor: 'pointer',
  color: 'var(--color-text-secondary)',
  borderRadius: '8px',
};

const bodyStyle: CSSProperties = {
  padding: '16px',
  textAlign: 'center',
};

const iconContainerStyle: CSSProperties = {
  marginBottom: '16px',
};

const messageStyle: CSSProperties = {
  fontSize: '1rem',
  color: 'var(--color-text-primary)',
  margin: '0 0 12px 0',
  lineHeight: 1.5,
};

const instructionStyle: CSSProperties = {
  fontSize: '0.9375rem',
  color: 'var(--color-text-secondary)',
  margin: '0 0 16px 0',
  lineHeight: 1.5,
};

const stepsContainerStyle: CSSProperties = {
  backgroundColor: 'var(--color-bg-secondary)',
  borderRadius: '12px',
  padding: '16px',
  textAlign: 'left',
};

const stepTitleStyle: CSSProperties = {
  fontSize: '0.875rem',
  fontWeight: 600,
  color: 'var(--color-text-primary)',
  margin: '0 0 12px 0',
};

const stepsListStyle: CSSProperties = {
  margin: 0,
  paddingLeft: '20px',
};

const stepStyle: CSSProperties = {
  fontSize: '0.875rem',
  color: 'var(--color-text-secondary)',
  marginBottom: '8px',
  lineHeight: 1.5,
};

const footerStyle: CSSProperties = {
  padding: '0 16px 16px 16px',
};

const dismissButtonStyle: CSSProperties = {
  width: '100%',
  padding: '14px',
  fontSize: '1rem',
  fontWeight: 500,
  color: 'var(--color-text-secondary)',
  backgroundColor: 'var(--color-bg-tertiary)',
  border: 'none',
  borderRadius: '12px',
  cursor: 'pointer',
};

export default IOSInstallPrompt;
